import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Shield, Lock } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import {
  useSignInApiCallMutation,
  useVerifyOtpApiCallMutation,
} from "../../services/api/auth/auth.api";
import { toast } from "sonner";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { ApiError } from "../../services/api/auth/auth.type";
import { LOCALSTORAGES } from "../../constants/constant";
import { useDispatch } from "react-redux";
import { baseApi } from "../../services/baseApi";

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [signIn] = useSignInApiCallMutation();
  const [verifyOtp] = useVerifyOtpApiCallMutation();

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    twoFactorCode: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [require2FA, setRequire2FA] = useState(false);
  const [otpMessage, setOtpMessage] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (!require2FA) {
        
        const data = await signIn({
          email: formData.email,
          password: formData.password,
        }).unwrap();

        if (data?.responseCode === 200) {
          setRequire2FA(true);
          setOtpMessage(data.responseMessage);
        }
      } else {
        const data = await verifyOtp({
          email: formData.email,
          otp: formData.twoFactorCode,
        }).unwrap();

        if (data.responseCode === 200) {
          toast.success(data.responseMessage);
          localStorage.setItem(LOCALSTORAGES.TOKEN, data?.result?.token);

          // Reset all cached queries so dashboard fetches fresh data
          dispatch(baseApi.util.resetApiState());
          navigate("/");
        }
      }
    } catch (error: unknown) {
      const fetchError = error as FetchBaseQueryError;
      const errorData = fetchError.data as ApiError;
      toast.error(errorData?.responseMessage || "Login failed");
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center space-y-2">
          <div className="mx-auto w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mb-4">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl">Airdrop Admin</CardTitle>
          <CardDescription>
            {require2FA
              ? "Enter your otp to continue"
              : "Sign in to your admin account"}
          </CardDescription>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {!require2FA ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="admin@example.com"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      value={formData.password}
                      onChange={handleInputChange}
                      required
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <Link
                    to="/forgot-password"
                    className="text-sm text-blue-600 hover:text-blue-500"
                  >
                    Forgot password?
                  </Link>
                </div>
              </>
            ) : (
              <div className="space-y-4">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <Lock className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-700">{otpMessage}</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="twoFactorCode">OTP</Label>
                  <Input
                    id="twoFactorCode"
                    name="twoFactorCode"
                    type="text"
                    placeholder="000000"
                    value={formData.twoFactorCode}
                    onChange={handleInputChange}
                    maxLength={6}
                    className="text-center text-lg tracking-widest"
                    required
                  />
                </div>

                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setRequire2FA(false)}
                  className="w-full"
                >
                  ← Back to login
                </Button>
              </div>
            )}

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Signing in...</span>
                </div>
              ) : require2FA ? (
                "Verify & Sign In"
              ) : (
                "Continue to Sign In"
              )}
            </Button>
          </form>

          {/* {!require2FA && (
            <>
              <Separator className="my-6" />
              <div className="text-center text-sm text-gray-600">
                <p>Demo Credentials:</p>
                <p className="font-mono">admin@airdrop.com / admin123</p>
              </div>
            </>
          )} */}
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
